import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { AlertCircle, CalendarClock, CheckCircle2, ChevronDown, ChevronRight, Download, History, Loader2, Plus, RotateCcw, Sparkles, Trash2, Undo2, Wallet } from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

/** A plan is named after the course it covers (a plan is always one course). */
const planTitle = (plan) =>
  (plan?.courses_snapshot || [])[0]?.title || "Pago aplazado";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const downloadBlob = async (url, filename, opts = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  return res;
};

/**
 * InstallmentPlanCard — pago aplazado UI for the "Cursos del usuario" modal.
 *
 * Two modes:
 *   · No plan → shows the "Activar pago aplazado" toggle. Admin picks the
 *     monthly quota (50/100/200/300 €) and ONE course — a plan is never a
 *     bundle and can't be extended later; the preview endpoint computes the
 *     plazos. On save, `_create_plan_for_user` runs server-side, auto-assigns
 *     the course and schedules plazos.
 *   · Active plan → renders the plazos table with a checkbox per row that
 *     any manager can flip to "paid" (which auto-creates a ticket for that
 *     plazo). Superadmin can also promote each plazo's ticket to a full
 *     invoice and cancel the plan.
 */
export default function InstallmentPlanCard({ user, courses, onPlanChanged }) {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const isManagerBypass =
    currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const canCreatePlan =
    isManagerBypass || !!(currentUser?.perms || {}).create_installment_plans;
  const [plan, setPlan] = useState(undefined); // undefined = loading, null = no plan, object = plan
  const [creating, setCreating] = useState(false);
  // When the previous plan is completed/cancelled the SA/admin can start a
  // new one on top of the historical view. This toggle reveals the form.
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);

  const reloadPlan = async () => {
    try {
      const { data } = await api.get(`/admin/users/${user.id}/installment-plan`);
      setPlan(data);
    } catch (err) {
      if (err.response?.status === 404) setPlan(null);
      else toast.error("No se pudo cargar el plan de pago");
    }
  };

  useEffect(() => {
    if (user?.id) reloadPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (plan === undefined) {
    return <div className="p-3 text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Cargando plan…</div>;
  }

  const planIsClosed = plan && (plan.status === "completed" || plan.status === "cancelled");

  return (
    <Card className="p-4 border border-amber-200 bg-amber-50/40 rounded-lg space-y-3" data-testid="installment-plan-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 text-amber-900 min-w-0">
          <Wallet className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            {/* A plan is named after the course it covers. */}
            <p className="text-sm font-semibold truncate" title={planTitle(plan)} data-testid="plan-title">
              {planTitle(plan)}
            </p>
            {plan && <p className="text-[11px] text-amber-800/80">Pago aplazado</p>}
          </div>
        </div>
        {plan?.is_blocked && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300">
            <AlertCircle className="w-3 h-3" /> Bloqueado por impago
          </span>
        )}
        {plan?.status === "completed" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
            <CheckCircle2 className="w-3 h-3" /> Plan completado
          </span>
        )}
        {plan?.status === "cancelled" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 border border-gray-300">
            <Trash2 className="w-3 h-3" /> Plan cancelado
          </span>
        )}
      </div>

      {!plan ? (
        canCreatePlan ? (
          <CreatePlanForm
            user={user}
            courses={courses}
            creating={creating}
            setCreating={setCreating}
            onCreated={async () => { await reloadPlan(); onPlanChanged?.(); }}
          />
        ) : (
          <p
            className="text-xs text-amber-900/80 bg-white border border-amber-200 rounded-md p-3"
            data-testid="installment-plan-no-perm"
          >
            Tu rol no tiene el permiso para crear planes de pago aplazado.
            Pide al superadmin que active <span className="font-medium">
            «Crear pago aplazado en alumno»</span> en tus permisos.
          </p>
        )
      ) : (
        <>
          {plan.status === "cancelled" ? (
            <CancelledPlanView
              plan={plan}
              onChanged={async () => { await reloadPlan(); onPlanChanged?.(); }}
            />
          ) : (
            <ActivePlanView
              plan={plan}
              isSuperadmin={isSuperadmin}
              onChanged={async () => { await reloadPlan(); onPlanChanged?.(); }}
            />
          )}
          {planIsClosed && canCreatePlan && (
            <div className="pt-3 border-t border-amber-200/70 space-y-3">
              {!showNewPlanForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewPlanForm(true)}
                  data-testid="installment-plan-new"
                  className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Crear un nuevo plan de pago aplazado
                </Button>
              ) : (
                <div className="rounded-md bg-white border border-amber-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                      Nuevo plan de pago aplazado
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewPlanForm(false)}
                      className="text-gray-500 hover:text-gray-700 h-6 px-2 text-[11px]"
                    >
                      Cancelar
                    </Button>
                  </div>
                  <CreatePlanForm
                    user={user}
                    courses={courses}
                    creating={creating}
                    setCreating={setCreating}
                    onCreated={async () => {
                      setShowNewPlanForm(false);
                      await reloadPlan();
                      onPlanChanged?.();
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ------------------ Create plan form ------------------ */
function CreatePlanForm({ user, courses, creating, setCreating, onCreated }) {
  const [enabled, setEnabled] = useState(false);
  // A plan covers exactly one course: single selection, never a list.
  const [selectedCourse, setSelectedCourse] = useState("");
  const [monthly, setMonthly] = useState(200);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  // "Código DTO": typed freely, only sent to the server once applied. Same
  // split as CartPricingSummary so both pickers behave identically.
  const [codeInput, setCodeInput] = useState("");
  const [code, setCode] = useState("");
  const [allTests, setAllTests] = useState(null); // fetched lazily when the toggle opens
  const [activePromos, setActivePromos] = useState([]);

  useEffect(() => {
    if (!enabled || allTests !== null) return;
    api
      .get("/admin/courses")
      .then((r) => setAllTests(r.data || []))
      .catch(() => setAllTests([]));
    // Only used to flag which courses carry a per-course discount. Packs are
    // deliberately not offered here: a plan is one course, never a bundle.
    api
      .get("/admin/promotions/active")
      .then((r) => setActivePromos(r.data || []))
      .catch(() => setActivePromos([]));
  }, [enabled, allTests]);

  // Pool of buyable courses: full course catalog with a numeric price. We
  // fall back to the assignments-only ``courses`` prop while the catalog loads.
  const catalog = (allTests || []).map((t) => ({
    course_id: t.id,
    title: t.title,
    price: t.price,
    kind: t.kind,
  }));
  const pool = catalog.length ? catalog : (courses || []);
  const eligible = pool.filter((c) => c.price != null && Number(c.price) > 0);

  const discountedIds = new Set(
    (activePromos || []).filter((p) => p.kind === "course" && p.course_id).map((p) => p.course_id),
  );

  useEffect(() => {
    if (!enabled || !selectedCourse) {
      setPreview(null);
      setPreviewErr("");
      return;
    }
    let cancel = false;
    setPreviewErr("");
    api
      .post(`/admin/installments/preview`, {
        monthly_amount: Number(monthly),
        course_id: selectedCourse,
        discount_code: code || null,
        // Needed to honour "one use per user" while previewing, so a code the
        // alumno already spent is caught here and not at the last click.
        user_id: user.id,
      })
      .then((r) => { if (!cancel) setPreview(r.data); })
      .catch((e) => { if (!cancel) { setPreview(null); setPreviewErr(e.response?.data?.detail || "Error"); } });
    return () => { cancel = true; };
  }, [enabled, monthly, selectedCourse, code, user.id]);

  const submit = async () => {
    if (!selectedCourse) {
      toast.error("Selecciona el curso del plan");
      return;
    }
    setCreating(true);
    try {
      await api.post(`/admin/users/${user.id}/installment-plan`, {
        monthly_amount: Number(monthly),
        course_id: selectedCourse,
        discount_code: code || null,
      });
      toast.success("Plan de pago aplazado creado");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el plan");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <label className="flex items-start gap-3 p-3 rounded-md bg-white border border-amber-200 cursor-pointer">
        <Checkbox
          checked={enabled}
          onCheckedChange={(v) => setEnabled(!!v)}
          className="mt-0.5"
          data-testid="plan-toggle"
        />
        <div>
          <p className="text-sm font-medium text-amber-900">Activar pago aplazado</p>
          <p className="text-[11px] text-amber-800/80 mt-0.5">
            Divide el precio de un curso en plazos mensuales. Cada plazo pagado genera un ticket automático.
          </p>
        </div>
      </label>
      {enabled && (
        <div className="space-y-3 pt-2">
          {/* Business rule, not a technical one: the plan exists for students
              who cannot pay by the usual means. Stated where it's decided. */}
          <p
            className="text-[11px] text-amber-900 bg-amber-100/70 border border-amber-300 rounded-md p-2"
            data-testid="plan-eligibility-notice"
          >
            <strong>Uso restringido:</strong> el pago aplazado es solo para alumnas y alumnos
            extracomunitarios sin permiso de residencia y/o de trabajo. Para el resto, usa
            «Generar pago», el botón de Sabadell o el pago manual.
          </p>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-1">Cuota mensual</p>
            <RadioGroup value={String(monthly)} onValueChange={(v) => setMonthly(v)} className="flex flex-wrap gap-3" data-testid="plan-monthly-group">
              {[50, 100, 200, 300].map((amt) => (
                <label key={amt} className="flex items-center gap-2 px-3 py-2 rounded-md border bg-white cursor-pointer hover:bg-amber-50">
                  <RadioGroupItem value={String(amt)} data-testid={`plan-monthly-${amt}`} />
                  <span className="text-sm font-mono-data">{amt} €/mes</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-1">
              Curso del plan
              {eligible.length === 0 && <span className="text-red-600 ml-2 font-normal normal-case">Ningún curso con precio configurado — añade precio primero.</span>}
            </p>
            <p className="text-[11px] text-amber-800/80 mb-1">
              Un plan cubre un único curso y no se puede ampliar después.
            </p>
            <RadioGroup
              value={selectedCourse}
              onValueChange={setSelectedCourse}
              className="max-h-40 overflow-y-auto space-y-1 border rounded-md bg-white p-2"
              data-testid="plan-course-list"
            >
              {eligible.map((c) => (
                <label key={c.course_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-amber-50 cursor-pointer">
                  <RadioGroupItem value={c.course_id} data-testid={`plan-course-${c.course_id}`} />
                  <span className="text-sm flex-1 truncate" title={c.title}>
                    {c.title}
                    {discountedIds.has(c.course_id) && (
                      <span className="ml-1 text-[10px] text-emerald-700">· en oferta</span>
                    )}
                  </span>
                  <span className="text-xs font-mono-data text-amber-900">{fmtEur(c.price)}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wide text-amber-900 font-semibold mb-1">
              Código DTO
            </p>
            <div className="flex gap-1 max-w-56">
              <Input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && setCode(codeInput.trim())}
                placeholder="Opcional"
                className="h-8 text-xs font-mono-data bg-white"
                data-testid="plan-discount-code"
              />
              {code ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 text-gray-500"
                  onClick={() => { setCode(""); setCodeInput(""); }}
                  data-testid="plan-discount-code-clear"
                >
                  Quitar
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2"
                  disabled={!codeInput.trim()}
                  onClick={() => setCode(codeInput.trim())}
                  data-testid="plan-discount-code-apply"
                >
                  Aplicar
                </Button>
              )}
            </div>
            <p className="text-[10px] text-amber-800/70 mt-1">
              El código se canjea al crear el plan. Cancelarlo después no lo devuelve.
            </p>
          </div>

          {preview?.code_error && (
            <p className="text-xs text-red-600" data-testid="plan-discount-code-error">
              {preview.code_error}
            </p>
          )}

          {preview && (
            <div className="bg-white rounded-md border border-amber-200 p-3" data-testid="plan-preview">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-amber-900 font-semibold uppercase">
                  Previsualización · {preview.installments.length} plazos
                </p>
                <p className="text-sm font-mono-data text-amber-900">
                  Total {fmtEur(preview.total_amount)}
                  {preview.discount_total > 0 && (
                    <span className="block text-[10px] text-emerald-700 text-right">
                      antes {fmtEur(preview.list_total)} · ahorro {fmtEur(preview.discount_total)}
                    </span>
                  )}
                </p>
              </div>
              {preview.code_applied && (
                <div
                  className="flex justify-between text-xs text-emerald-700 mb-2 pb-2 border-b border-gray-100"
                  data-testid="plan-code-applied"
                >
                  <span>Código {preview.code_applied.code}</span>
                  <span className="font-mono-data">−{fmtEur(preview.code_applied.amount)}</span>
                </div>
              )}
              <ul className="space-y-0.5">
                {preview.installments.map((i) => (
                  <li key={i.n} className="flex justify-between text-xs font-mono-data text-gray-700">
                    <span>Plazo {i.n} · vence {fmtDate(i.due_date)}</span>
                    <span>{fmtEur(i.amount_eur)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {previewErr && <p className="text-xs text-red-600">{previewErr}</p>}

          <Button
            onClick={submit}
            disabled={creating || !selectedCourse}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="plan-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-2" />}
            Crear plan de pago aplazado
          </Button>
        </div>
      )}
    </>
  );
}

/* ------------------ Cancelled plan view (read-only + reactivate) ------------------ */
function CancelledPlanView({ plan, onChanged }) {
  const { user: currentUser } = useAuth();
  // Cancelling is superadmin-only; undoing that mistake is also open to
  // admin, since it restores access rather than taking it away.
  const canReactivate =
    currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const paid = plan.installments.filter((i) => i.status === "paid");
  const pending = plan.installments.filter((i) => i.status !== "paid");
  const paidTotal = paid.reduce((s, i) => s + Number(i.amount_eur || 0), 0);
  const pendingTotal = pending.reduce((s, i) => s + Number(i.amount_eur || 0), 0);

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/installments/${plan.id}/reactivate`);
      toast.success("Plan reactivado — ya se pueden registrar cobros de nuevo");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reactivar el plan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-gray-300 bg-white/70" data-testid="plan-cancelled-view">
      <div className="flex items-center justify-between gap-2 p-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900"
          data-testid="plan-cancelled-toggle"
        >
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Plan cancelado — ver en qué estado quedó
        </button>
        {canReactivate && (
          <Button
            variant="outline"
            size="sm"
            onClick={reactivate}
            disabled={busy}
            className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100"
            data-testid="plan-reactivate"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
            Reactivar plan
          </Button>
        )}
      </div>

      <div className="px-3 pb-3 text-[11px] text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
        <span>Cobrado: <strong className="font-mono-data">{fmtEur(paidTotal)} ({paid.length}/{plan.installments.length})</strong></span>
        <span>Quedó sin cobrar: <strong className="font-mono-data">{fmtEur(pendingTotal)} ({pending.length})</strong></span>
        {plan.discount_code && (
          <span data-testid="plan-applied-code">
            Código: <strong className="font-mono-data">{plan.discount_code}</strong>
            {plan.code_discount_eur ? ` (−${fmtEur(plan.code_discount_eur)})` : ""}
          </span>
        )}
        {plan.cancelled_at && <span>Cancelado el {fmtDate(plan.cancelled_at)}</span>}
      </div>

      {open && (
        <div className="px-3 pb-3 overflow-x-auto">
          <table className="w-full text-sm bg-white border border-gray-200 rounded" data-testid="plan-cancelled-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-600 border-b bg-gray-50">
                <th className="text-left py-2 px-2 w-10">#</th>
                <th className="text-left py-2 px-2">Vencía</th>
                <th className="text-right py-2 px-2">Importe</th>
                <th className="text-left py-2 px-2">Estado al cancelar</th>
                <th className="text-left py-2 px-2">Nº ticket</th>
              </tr>
            </thead>
            <tbody>
              {plan.installments.map((i) => (
                <tr key={i.n} className="border-b border-gray-100" data-testid={`plan-cancelled-installment-${i.n}`}>
                  <td className="py-2 px-2 font-mono-data">{i.n}</td>
                  <td className="py-2 px-2 text-gray-700">{fmtDate(i.due_date)}</td>
                  <td className="py-2 px-2 text-right font-mono-data">{fmtEur(i.amount_eur)}</td>
                  <td className="py-2 px-2">
                    {i.status === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">
                        <CheckCircle2 className="w-3 h-3" /> Cobrado
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                        Sin cobrar
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 font-mono-data text-brand-ink text-xs">{i.ticket_number || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-500 mt-2">
            Mientras el plan esté cancelado no se pueden registrar cobros ni anular los ya
            emitidos. Los tickets cobrados siguen siendo válidos y constan en el histórico.
          </p>
        </div>
      )}
    </div>
  );
}


/* ------------------ Active plan view ------------------ */
function ActivePlanView({ plan, isSuperadmin, onChanged }) {
  const { user: currentUser } = useAuth();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelPayment, setConfirmCancelPayment] = useState(null); // installment being cancelled, or null
  const [promoteTx, setPromoteTx] = useState(null);
  const [busy, setBusy] = useState(null);

  const markPaid = async (n) => {
    if (busy) return;
    setBusy(`pay-${n}`);
    try {
      await api.post(`/admin/installments/${plan.id}/pay/${n}`);
      toast.success(`Plazo ${n} marcado como pagado (ticket generado)`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al marcar el plazo");
    } finally {
      setBusy(null);
    }
  };

  const downloadPlazoTicket = async (txId, ticketNumber) => {
    try {
      await downloadBlob(
        `${API}/admin/invoices/${txId}/pdf`,
        `ticket-${ticketNumber || txId}.pdf`,
      );
    } catch (e) {
      toast.error(e.message);
    }
  };

  const cancelPayment = async (n) => {
    setBusy(`cancel-pay-${n}`);
    try {
      await api.post(`/admin/installments/${plan.id}/pay/${n}/cancel`);
      toast.success(`Pago del plazo ${n} cancelado — vuelve a pendiente`);
      setConfirmCancelPayment(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar el pago");
    } finally {
      setBusy(null);
    }
  };

  const cancelPlan = async () => {
    setBusy("cancel");
    try {
      await api.delete(`/admin/installments/${plan.id}`);
      toast.success("Plan cancelado");
      setConfirmCancel(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cancelar");
    } finally {
      setBusy(null);
    }
  };

  const paidCount = plan.installments.filter((i) => i.status === "paid").length;
  const totalPaid = plan.installments
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + Number(i.amount_eur || 0), 0);

  // Plazos are charged strictly in order (the backend enforces it too): only
  // the first outstanding one can be marked paid, and only the last paid one
  // can be undone, so the plan never ends up with a hole in the middle.
  const nextPayable = plan.installments.find((i) => i.status !== "paid")?.n ?? null;
  const lastPaid = plan.installments
    .filter((i) => i.status === "paid")
    .reduce((max, i) => Math.max(max, i.n), 0) || null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-amber-900">
        <span>Cuota mensual: <strong className="font-mono-data">{fmtEur(plan.monthly_amount)}</strong></span>
        <span>Total: <strong className="font-mono-data">{fmtEur(plan.total_amount)}</strong></span>
        <span>Pagado: <strong className="font-mono-data">{fmtEur(totalPaid)} ({paidCount}/{plan.installments.length})</strong></span>
        {plan.discount_code && (
          <span data-testid="plan-applied-code">
            Código: <strong className="font-mono-data">{plan.discount_code}</strong>
            {plan.code_discount_eur ? ` (−${fmtEur(plan.code_discount_eur)})` : ""}
          </span>
        )}
        <span>Creado: {fmtDate(plan.created_at)}</span>
      </div>

      {nextPayable !== null && (
        <p className="text-[11px] text-amber-800/80" data-testid="plan-order-hint">
          Los plazos se cobran en orden: ahora toca el <strong>plazo {nextPayable}</strong>.
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm bg-white border border-amber-200 rounded" data-testid="plan-installments-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-600 border-b bg-amber-50">
              <th className="text-left py-2 px-2 w-10">#</th>
              <th className="text-left py-2 px-2">Vence</th>
              <th className="text-right py-2 px-2">Importe</th>
              <th className="text-left py-2 px-2">Estado</th>
              <th className="text-left py-2 px-2">Nº ticket</th>
              <th className="text-right py-2 px-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plan.installments.map((i) => (
              <tr key={i.n} className="border-b border-amber-100" data-testid={`plan-installment-${i.n}`}>
                <td className="py-2 px-2 font-mono-data">{i.n}</td>
                <td className="py-2 px-2 text-gray-700">{fmtDate(i.due_date)}</td>
                <td className="py-2 px-2 text-right font-mono-data">{fmtEur(i.amount_eur)}</td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {i.status === "paid" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`plan-status-paid-${i.n}`}>
                        <CheckCircle2 className="w-3 h-3" /> Pagado
                      </span>
                    ) : i.status === "overdue" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-300">
                        <AlertCircle className="w-3 h-3" /> Vencido
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                        Pendiente
                      </span>
                    )}
                    {i.cancellations?.length > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500"
                        title={i.cancellations
                          .map((c) => `Cancelado por ${c.by_email || c.by_id} el ${fmtDate(c.at)} (ticket ${c.previous_ticket_number || "—"})`)
                          .join("\n")}
                        data-testid={`plan-cancellations-${i.n}`}
                      >
                        <History className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-2 px-2 font-mono-data text-brand-ink text-xs">{i.ticket_number || "—"}</td>
                <td className="py-2 px-2 text-right space-x-1">
                  {i.status !== "paid" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markPaid(i.n)}
                      disabled={busy === `pay-${i.n}` || i.n !== nextPayable}
                      data-testid={`plan-pay-${i.n}`}
                      title={
                        i.n !== nextPayable
                          ? `Cobra antes el plazo ${nextPayable}: los plazos se cobran en orden`
                          : "Marcar este plazo como pagado"
                      }
                      className="border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-40"
                    >
                      {busy === `pay-${i.n}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Marcar pagado"}
                    </Button>
                  ) : (
                    <>
                      {i.tx_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadPlazoTicket(i.tx_id, i.ticket_number)}
                          data-testid={`plan-download-${i.n}`}
                          title="Descargar ticket"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {isSuperadmin && i.tx_id && !i.invoice_number && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPromoteTx(i)}
                          data-testid={`plan-invoice-${i.n}`}
                          title="Generar factura"
                          className="text-purple-700 hover:bg-purple-50"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {i.invoice_number && (
                        <span className="text-[10px] font-mono-data text-purple-700 px-1">
                          {i.invoice_number}
                        </span>
                      )}
                      {i.invoice_number ? (
                        <span
                          className="inline-block text-gray-300"
                          title="Ya tiene factura emitida — la cancelación se gestiona desde facturación"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </span>
                      ) : i.n !== lastPaid ? (
                        <span
                          className="inline-block text-gray-300"
                          title={`Anula antes el cobro del plazo ${lastPaid}: los plazos se anulan del último al primero`}
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmCancelPayment(i.n)}
                          data-testid={`plan-cancel-payment-${i.n}`}
                          title="Cancelar este pago (marcado por error)"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isSuperadmin && plan.status === "active" && (
        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmCancel(true)}
            className="text-red-600 hover:bg-red-50"
            data-testid="plan-cancel"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancelar plan
          </Button>
        </div>
      )}

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar el plan de «{planTitle(plan)}»?</DialogTitle>
            <DialogDescription>
              El alumno mantendrá el acceso al curso (la asignación no se borra) y los tickets ya emitidos permanecen en el histórico. El plan queda archivado y no admite más cobros; un admin o superadmin puede reactivarlo más adelante.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmCancel(false)}>Volver</Button>
            <Button
              onClick={cancelPlan}
              disabled={busy === "cancel"}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="plan-cancel-confirm"
            >
              Sí, cancelar plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancelPayment != null} onOpenChange={(o) => !o && setConfirmCancelPayment(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Cancelar el pago del plazo {confirmCancelPayment}?</DialogTitle>
            <DialogDescription>
              El plazo vuelve a quedar pendiente y se podrá marcar como pagado de nuevo más tarde. El ticket ya
              emitido se anula (queda marcado como cancelado, no se borra) y quedará registrado quién canceló
              este pago y cuándo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmCancelPayment(null)}>Volver</Button>
            <Button
              onClick={() => cancelPayment(confirmCancelPayment)}
              disabled={busy === `cancel-pay-${confirmCancelPayment}`}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="plan-cancel-payment-confirm"
            >
              Sí, cancelar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromoteInstallmentInvoice
        plazo={promoteTx}
        plan={plan}
        onClose={() => setPromoteTx(null)}
        onDone={() => { setPromoteTx(null); onChanged(); }}
      />
    </div>
  );
}

function PromoteInstallmentInvoice({ plazo, plan, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", tax_id: "",
    address: "", postal_code: "", city: "", country: "ES",
  });

  useEffect(() => {
    if (plazo) {
      // Prefill from user (would need the user object; keep empty and let SA fill)
      setForm({
        first_name: "", last_name: "", email: "", tax_id: "",
        address: "", postal_code: "", city: "", country: "ES",
      });
    }
  }, [plazo]);

  if (!plazo) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await downloadBlob(
        `${API}/admin/tx/${plazo.tx_id}/generate-invoice`,
        `factura.pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      const invNo = res.headers.get("X-Invoice-Number");
      toast.success(`Factura ${invNo || ""} emitida`);
      // Update plazo.invoice_number locally-ish via reload.
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!plazo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="plan-promote-modal">
        <DialogHeader>
          <DialogTitle>Emitir factura del plazo {plazo.n}</DialogTitle>
          <DialogDescription>
            Se generará una factura correlativa (AÑO-Fnnnn) vinculada al ticket {plazo.ticket_number}. Rellena los datos del cliente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">Nombre</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="plan-invoice-firstname" />
            </div>
            <div>
              <label className="text-xs">Apellidos / Razón social</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="plan-invoice-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs">CIF/NIF/NIE</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <label className="text-xs">Email</label>
              <input className="w-full border rounded px-2 py-1 text-sm" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs">Dirección</label>
            <input className="w-full border rounded px-2 py-1 text-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs">C.P.</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="text-xs">Ciudad</label>
              <input className="w-full border rounded px-2 py-1 text-sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-purple-700 hover:bg-purple-800 text-white"
            data-testid="plan-invoice-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Emitir factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
